import React, { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import {
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Container,
  Grid,
  MenuItem,
  TextField,
  Typography,
} from '../../../../../components/mui/Core/index'
import PageTitle from '../../../../../components/PageTitle/page-title'
import Constants from '../../../../../constants/en.json'
import GenericInputCardRenderer from '../../../RoleManagement/components/GenericInputCardRenderer/GenericInputCardRenderer'
import { createPermission, updatePermission } from './update-permission-slice'
import { useApplicationList } from '../../../../../hooks/applications/application-list'
import { setSnackbar } from '../../../../../components/GenericSnackbar/generic-snackbar-slice'
import { fetchRoleApplication } from '../../../RoleManagement/components/PermissionsList/permissions-list-slice'

function UpdatePermission() {
  const location = useLocation()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const applicationList = useApplicationList()

  const isEditingPermission = location?.state?.isEditingPermission
  const selectedPermission = location?.state?.selectedPermission

  const permissionApplicationList = useSelector((state) => state?.permissions?.roleApplication)
  const isUpdatingPermission = useSelector(
    (state) => state?.permissionManagement?.updatePermission?.isUpdatingPermission,
  )

  useEffect(() => {
    dispatch(fetchRoleApplication())
  }, [dispatch])

  const [permissionCode, setPermissionCode] = useState('')
  const [permissionDescription, setPermissionDescription] = useState('')
  const [permissionApplication, setPermissionApplication] = useState('')

  const isDetailsFilled =
    permissionCode?.trim()?.length > 0 &&
    permissionDescription?.trim()?.length > 0 &&
    permissionApplication?.trim()?.length > 0

  useEffect(() => {
    if (isEditingPermission && selectedPermission) {
      setPermissionCode(selectedPermission?.code)
      setPermissionDescription(selectedPermission?.description)
      setPermissionApplication(selectedPermission?.application)
    }
  }, [selectedPermission, isEditingPermission])

  const handleExitPermission = () => navigate('/role-management/manage-permissions')

  const handlePermissionCodeChange = (event) => setPermissionCode(event.target.value)
  const handlePermissionDescriptionChange = (event) => setPermissionDescription(event.target.value)
  const handlePermissionApplicationChange = (event) => setPermissionApplication(event.target.value)

  const handleUpdatePermission = async () => {
    if (!isDetailsFilled) return
    const payload = {
      code: permissionCode,
      description: permissionDescription,
      application: permissionApplication,
    }

    try {
      if (isEditingPermission) {
        await dispatch(updatePermission(payload)).unwrap()
        dispatch(
          setSnackbar({
            message:
              Constants.roleManagement.permissionManagement.createPermission.SUCCESS_EDIT_PERMISSION.replace(
                '__APP__',
                applicationList[permissionApplication],
              ),
            severity: 'success',
            action: true,
            autoHideDuration: 3000,
          }),
        )
      } else {
        await dispatch(createPermission(payload)).unwrap()
        dispatch(
          setSnackbar({
            message:
              Constants.roleManagement.permissionManagement.createPermission.SUCCESS_CREATE_PERMISSION.replace(
                '__APP__',
                applicationList[permissionApplication],
              ),
            severity: 'success',
            action: true,
            autoHideDuration: 3000,
          }),
        )
      }

      handleExitPermission()
    } catch (error) {
      dispatch(
        setSnackbar({
          message: error?.message,
          severity: 'error',
          action: true,
          autoHideDuration: 3000,
        }),
      )
    }
  }

  const headContainerStyle = { alignItems: 'center', marginBottom: '20px' }
  const headButtonsContainerStyle = { display: 'flex', justifyContent: 'flex-end', gap: 3 }

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Backdrop open={isUpdatingPermission}>
        <CircularProgress color='inherit' />
      </Backdrop>

      <Container maxWidth='xl'>
        <Grid container spacing={2} sx={headContainerStyle}>
          <Grid size={{ xs: 8 }}>
            <PageTitle
              title={
                isEditingPermission
                  ? Constants.roleManagement.permissionManagement.createPermission.EDIT_PERMISSION
                  : Constants.roleManagement.permissionManagement.createPermission.CREATE_PERMISSION
              }
            />
          </Grid>
          <Grid size={{ xs: 4 }} sx={headButtonsContainerStyle}>
            <Button variant='outlined' color='secondary' onClick={handleExitPermission}>
              {Constants.roleManagement.permissionManagement.createPermission.EXIT}
            </Button>
            <Button
              variant='contained'
              color='primary'
              disabled={!isDetailsFilled}
              onClick={handleUpdatePermission}
            >
              {Constants.roleManagement.permissionManagement.createPermission.SUBMIT}
            </Button>
          </Grid>
        </Grid>

        <Grid container spacing={4}>
          <Grid size={{ xs: 4 }}>
            <GenericInputCardRenderer
              title={Constants.roleManagement.permissionManagement.createPermission.PERMISSION_CODE}
            >
              {isEditingPermission && <Typography>{permissionCode}</Typography>}
              {!isEditingPermission && (
                <TextField
                  type='text'
                  fullWidth
                  value={permissionCode}
                  label={
                    Constants.roleManagement.permissionManagement.createPermission.PERMISSION_CODE
                  }
                  placeholder={
                    Constants.roleManagement.permissionManagement.createPermission.PERMISSION_CODE
                  }
                  onChange={handlePermissionCodeChange}
                />
              )}
            </GenericInputCardRenderer>
          </Grid>
          <Grid size={{ xs: 4 }}>
            <GenericInputCardRenderer
              title={
                Constants.roleManagement.permissionManagement.createPermission
                  .PERMISSION_DESCRIPTION
              }
            >
              <TextField
                type='text'
                fullWidth
                value={permissionDescription}
                label={
                  Constants.roleManagement.permissionManagement.createPermission
                    .PERMISSION_DESCRIPTION
                }
                placeholder={
                  Constants.roleManagement.permissionManagement.createPermission
                    .PERMISSION_DESCRIPTION
                }
                onChange={handlePermissionDescriptionChange}
              />
            </GenericInputCardRenderer>
          </Grid>
          <Grid size={{ xs: 4 }}>
            <GenericInputCardRenderer
              title={
                Constants.roleManagement.permissionManagement.createPermission
                  .PERMISSION_APPLICATION
              }
            >
              {isEditingPermission && (
                <Typography>{applicationList[permissionApplication]}</Typography>
              )}
              {!isEditingPermission && (
                <TextField
                  select
                  fullWidth
                  value={permissionApplication}
                  label={
                    Constants.roleManagement.permissionManagement.createPermission
                      .PERMISSION_APPLICATION
                  }
                  onChange={handlePermissionApplicationChange}
                >
                  {permissionApplicationList?.map((application) => (
                    <MenuItem key={application.name} value={application.name}>
                      {application.description}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            </GenericInputCardRenderer>
          </Grid>
        </Grid>
      </Container>
    </Box>
  )
}

export default UpdatePermission
